import React from 'react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import { Plus, X, RotateCcw } from 'lucide-react';
import {
  BannerVisualOverride,
  BannerVisualStyle,
  resolveBannerVisual,
  BannerPreset,
  BannerCustomStyle,
} from '../../../api/banners';

interface BannerColorEditorProps {
  isDark: boolean;
  /** Какую тему приложения редактируем. */
  mode: 'light' | 'dark';
  preset: BannerPreset;
  custom?: BannerCustomStyle | null;
  onChange: (next: BannerVisualOverride | null) => void;
}

const FIELDS: Array<{ key: keyof BannerVisualStyle; label: string }> = [
  { key: 'fg', label: 'Заголовок' },
  { key: 'muted', label: 'Подзаголовок' },
  { key: 'iconBg', label: 'Фон иконки' },
  { key: 'iconFg', label: 'Цвет иконки' },
];

/**
 * Ручные цвета поверх пресета для одной темы. Пустое поле = берём из пресета,
 * поэтому админ может поменять только фон, не трогая остальное.
 */
export function BannerColorEditor({
  isDark,
  mode,
  preset,
  custom,
  onChange,
}: BannerColorEditorProps) {
  const override = (mode === 'dark' ? custom?.dark : custom?.light) ?? {};
  // Значения из пресета — их показываем как плейсхолдеры, чтобы было видно,
  // что именно перекрывается.
  const base = resolveBannerVisual(preset, mode === 'dark', null);

  const colors = override.colors?.length ? override.colors : base.colors;
  const hasOverride = Object.values(override).some(
    (v) => v !== undefined && v !== null && v !== '',
  );

  const patch = (next: Partial<BannerVisualOverride>) => {
    const merged = { ...override, ...next };
    const cleaned = Object.fromEntries(
      Object.entries(merged).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== '',
      ),
    ) as BannerVisualOverride;
    onChange(Object.keys(cleaned).length > 0 ? cleaned : null);
  };

  const setColorStop = (index: number, value: string) => {
    const next = [...colors];
    next[index] = value;
    patch({ colors: next });
  };

  const labelCls = cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500');

  return (
    <div
      className={cn(
        'space-y-3 rounded-xl border p-3',
        isDark ? 'border-slate-700' : 'border-slate-200',
      )}
    >
      <div className="flex items-center justify-between">
        <Label className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
          {mode === 'dark' ? 'Тёмная тема' : 'Светлая тема'}
        </Label>
        {hasOverride && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onChange(null)}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Вернуть пресет
          </Button>
        )}
      </div>

      {/* Фон: 1 цвет — заливка, 2+ — градиент */}
      <div className="space-y-1.5">
        <span className={labelCls}>
          Фон {colors.length > 1 ? `(градиент, ${colors.length} цвета)` : '(заливка)'}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {colors.map((color, index) => (
            <div key={index} className="flex items-center gap-1">
              <input
                type="color"
                value={normalizeHex(color)}
                onChange={(e) => setColorStop(index, e.target.value)}
                className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                title={color}
              />
              {colors.length > 1 && (
                <button
                  type="button"
                  onClick={() => patch({ colors: colors.filter((_, i) => i !== index) })}
                  className="text-slate-400 hover:text-red-500"
                  title="Убрать цвет"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
          {colors.length < 4 && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => patch({ colors: [...colors, colors[colors.length - 1]] })}
            >
              <Plus className="mr-1 h-3 w-3" />
              Цвет
            </Button>
          )}
        </div>
      </div>

      {/* Остальные цвета */}
      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map(({ key, label }) => {
          const value = override[key as keyof BannerVisualOverride] as string | undefined;
          const fallback = base[key] as string;
          return (
            <div key={key} className="space-y-1">
              <span className={labelCls}>{label}</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="color"
                  value={normalizeHex(value || fallback)}
                  onChange={(e) => patch({ [key]: e.target.value })}
                  className="h-8 w-10 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                />
                <Input
                  value={value ?? ''}
                  placeholder={fallback}
                  onChange={(e) => patch({ [key]: e.target.value })}
                  className={cn(
                    'h-8 text-xs',
                    isDark
                      ? 'bg-slate-800 border-slate-700 text-white'
                      : 'bg-white border-slate-200',
                  )}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * <input type="color"> понимает только #rrggbb. Полупрозрачные значения из
 * пресетов (rgba) он бы обнулил в чёрный, поэтому для них подставляем серый —
 * точное значение всё равно правится текстовым полем.
 */
function normalizeHex(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#888888';
}
